import { useState } from "react";
import { Copy, Download, FileText, MessageCircle, MessageSquareText, Share2 } from "lucide-react";
import { toast } from "sonner";
import { waLink } from "@/lib/api";
import { Modal } from "@/components/common";

// makePdf() -> Promise<File>; savePdf() downloads; wa = { phone, text }
export default function SendReceipt({ open, onOpenChange, makePdf, savePdf, wa }) {
  const [mode, setMode] = useState(null);
  const [busy, setBusy] = useState(false);
  const close = (o) => { if (!o) setMode(null); onOpenChange(o); };
  const shareFile = async () => {
    setBusy(true);
    try {
      const f = await makePdf();
      if (!navigator.canShare?.({ files: [f] })) { toast.info("مشاركة الملفات غير مدعومة في هذا المتصفح – نزّل الملف ثم أرفقه يدويًا"); return; }
      await navigator.share({ files: [f], title: f.name });
      toast.success("فُتحت قائمة المشاركة – أكمل الإرسال من التطبيق الذي اخترته");
    } catch (e) { if (e?.name !== "AbortError") toast.error("تعذرت المشاركة"); } finally { setBusy(false); }
  };
  const copy = async () => {
    try { await navigator.clipboard.writeText(wa.text); toast.success("تم نسخ نص الرسالة – الصقه في التطبيق الذي تريد"); } catch { toast.error("تعذر النسخ، حدد النص وانسخه يدويًا"); }
  };
  const shareText = async () => {
    if (!navigator.share) { toast.info("المشاركة غير مدعومة هنا – استعمل النسخ"); return; }
    try { await navigator.share({ text: wa.text }); toast.success("فُتحت قائمة المشاركة – أكمل الإرسال من هناك"); } catch (e) { if (e?.name !== "AbortError") toast.error("تعذرت المشاركة"); }
  };
  const choice = (m, Icon, title, sub) => (
    <button type="button" onClick={() => setMode(m)} data-testid={`send-mode-${m}`} className="w-full rounded-xl border-2 p-4 text-right flex items-center gap-3 hover:border-[#1A4D2E] transition-colors">
      <Icon className="w-7 h-7 text-[#1A4D2E] shrink-0" /><div><div className="font-bold">{title}</div><div className="text-xs text-slate-500">{sub}</div></div>
    </button>
  );
  return (
    <Modal open={open} onOpenChange={close} title="إرسال الوصل">
      <div className="space-y-3" data-testid="send-receipt-dialog">
        {!mode && <>
          <p className="text-sm text-slate-600">اختر طريقة الإرسال:</p>
          {choice("pdf", FileText, "إرسال PDF", "ملف الوصل الرسمي بنفس التصميم ورمز QR")}
          {choice("text", MessageSquareText, "إرسال رسالة نصية", "ملخص الوصل كنص يُنسخ أو يُشارك عبر واتساب والرسائل")}
        </>}
        {mode === "pdf" && <>
          <button className="k-btn-accent w-full" onClick={savePdf} data-testid="send-pdf-download"><Download className="w-5 h-5" /> تنزيل ملف PDF</button>
          <button className="k-btn-primary w-full" onClick={shareFile} disabled={busy} data-testid="send-pdf-share"><Share2 className="w-5 h-5" /> {busy ? "جارٍ تحضير الملف…" : "مشاركة الملف عبر تطبيقات الهاتف"}</button>
        </>}
        {mode === "text" && <>
          <textarea readOnly className="k-input h-48 text-sm leading-6" value={wa.text} data-testid="send-text-preview" />
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            <button className="k-btn-outline" onClick={copy} data-testid="send-text-copy"><Copy className="w-4 h-4" /> نسخ الرسالة</button>
            {wa.phone && <a className="k-btn-outline text-emerald-700" href={waLink(wa.phone, wa.text)} target="_blank" rel="noopener noreferrer" onClick={() => toast.info("فُتح واتساب – اضغط إرسال هناك لإرسال الرسالة فعليًا")} data-testid="send-text-whatsapp"><MessageCircle className="w-4 h-4" /> واتساب الزبون</a>}
            <button className="k-btn-outline" onClick={shareText} data-testid="send-text-share"><Share2 className="w-4 h-4" /> تطبيقات أخرى</button>
          </div>
        </>}
        {mode && <p className="text-xs text-slate-500">لا تُرسل الرسالة إلا عندما تضغط «إرسال» داخل التطبيق الذي يُفتح.</p>}
        {mode && <button className="text-sm underline text-slate-500" onClick={() => setMode(null)} data-testid="send-back">رجوع لاختيار الطريقة</button>}
      </div>
    </Modal>
  );
}
